/**
 * Operator and admin actions (M6.6): a confirm dialog before anything that changes the record,
 * a notification after, and the affected queries refreshed. Errors show the API's own message
 * (and a CSV's bad lines).
 */
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import type { QueryClient } from "@tanstack/react-query";

import { ApiError } from "../api/fetcher";

const CLOSE = { "aria-label": "Close" };

export function errorText(e: unknown): string {
  if (e instanceof ApiError) {
    const lines = e.body["errors"];
    if (Array.isArray(lines) && lines.length > 0) {
      return (
        `${e.message}: ` +
        lines
          .slice(0, 8)
          .map((x) => {
            const r = x as Record<string, unknown>;
            return r["line"] !== undefined ? `line ${String(r["line"])}: ${String(r["error"])}` : `${String(r["loc"])}: ${String(r["msg"])}`;
          })
          .join("; ")
      );
    }
    if (Array.isArray(e.detail)) return (e.detail as { msg?: string }[]).map((d) => d.msg ?? "").join("; ");
    return e.message;
  }
  return e instanceof Error ? e.message : String(e);
}

export async function act<T>(
  run: () => Promise<T>,
  { done, qc, refresh = [] }: { done: string; qc?: QueryClient; refresh?: string[] },
): Promise<T | undefined> {
  try {
    const out = await run();
    notifications.show({ color: "teal", message: done, closeButtonProps: CLOSE });
    for (const key of refresh) void qc?.invalidateQueries({ predicate: (q) => String(q.queryKey[0]).includes(key) });
    return out;
  } catch (e) {
    notifications.show({ color: "red", title: "Not done", message: errorText(e), autoClose: 12_000, closeButtonProps: CLOSE });
    return undefined;
  }
}

/** Ask first: `onConfirm` runs only after "Yes" (red for what cannot be undone). */
export function confirm(title: string, body: string, onConfirm: () => void, label = "Yes", danger = false): void {
  modals.openConfirmModal({
    title,
    children: body,
    labels: { confirm: label, cancel: "Cancel" },
    confirmProps: danger ? { color: "red" } : undefined,
    onConfirm,
  });
}
